// Textes des pages légales : mentions légales, politique de confidentialité, conditions de vente.
// Un module par langue (legal-<langue>.ts), chargé à l'ouverture d'une page légale seulement. Le français fait foi ;
// l'anglais est relu, les autres langues sont des traductions à relire par un locuteur natif.
// Les champs entre accolades viennent de `ui/legalInfo.ts` ({owner}, {email}…) ; {notice}, {privacy} et {terms}
// deviennent des liens vers les autres pages, {updated} la date de la version.
import type { Lang } from './index';

/** Paragraphe, liste à puces, ou tableau (première ligne : en-têtes). */
export type LegalBlock = string | { list: string[] } | { table: string[][] };

export interface LegalSection {
  /** Identifiant stable, le même dans toutes les langues. */
  id: string;
  title: string;
  body: LegalBlock[];
}

export interface LegalDoc {
  title: string;
  /** Résumé sous le titre. */
  lead: string;
  sections: LegalSection[];
}

export interface LegalTexts {
  notice: LegalDoc;
  privacy: LegalDoc;
  terms: LegalDoc;
}

export type LegalDocId = keyof LegalTexts;

const LOADERS: Record<Lang, () => Promise<{ legal: LegalTexts }>> = {
  fr: () => import('./legal-fr'),
  en: () => import('./legal-en'),
  es: () => import('./legal-es'),
  'pt-BR': () => import('./legal-pt-BR'),
  it: () => import('./legal-it'),
  de: () => import('./legal-de'),
  ru: () => import('./legal-ru'),
  'zh-Hans': () => import('./legal-zh-Hans'),
  ja: () => import('./legal-ja'),
  ko: () => import('./legal-ko'),
  ar: () => import('./legal-ar'),
  hi: () => import('./legal-hi'),
};

const cache = new Map<Lang, Promise<LegalTexts>>();
const loaded = new Map<Lang, LegalTexts>();

/** Textes légaux d'une langue ; en cas d'échec de chargement, la version française. */
export function loadLegal(lang: Lang): Promise<LegalTexts> {
  let p = cache.get(lang);
  if (!p) {
    p = LOADERS[lang]()
      .then((m) => {
        loaded.set(lang, m.legal);
        return m.legal;
      })
      .catch(() => {
        cache.delete(lang);
        return lang === 'fr' ? Promise.reject(new Error('legal-fr')) : loadLegal('fr');
      });
    cache.set(lang, p);
  }
  return p;
}

/** Textes déjà chargés (sans attendre) : évite un écran « Chargement… » en passant d'une page légale à l'autre. */
export function peekLegal(lang: Lang): LegalTexts | null {
  return loaded.get(lang) ?? null;
}
